import { useMemo } from 'react';

import { CameraIcon, CircleNotchIcon } from '@phosphor-icons/react/dist/ssr';
import classNames from 'classnames';

import SubscriptionAvatar from '@/shared/subscriptionAvatar';

import type { ImageUploadProps } from './ImageUpload.types';
import { useImageUpload } from './useImageUpload';

export function ImageUpload({
  currentImageUrl,
  onFileSelect,
  shape = 'circle',
  size = 64,
  accept,
  disabled = false,
  isLoading = false,
  label,
  helperText,
  className,
  name = 'avatar',
}: ImageUploadProps) {
  const { inputId, previewUrl, open, inputProps } = useImageUpload({
    onFileSelect,
    accept,
    disabled: disabled || isLoading,
    name,
  });

  const displayUrl = previewUrl || currentImageUrl;
  const isCircle = shape === 'circle';
  const hasImage = !!displayUrl;

  const imageElement = useMemo(() => {
    if (previewUrl) {
      return <SubscriptionAvatar avatarUrl={previewUrl} />;
    }

    return <SubscriptionAvatar avatarUrl={displayUrl} />;
  }, [previewUrl, displayUrl]);

  return (
    <div className={classNames('space-y-1', className)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={open}
          disabled={disabled || isLoading}
          className={classNames(
            'relative overflow-hidden transition-colors',
            'focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            isCircle ? 'rounded-full' : 'rounded-lg',
            hasImage
              ? 'border-0'
              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100',
          )}
          style={{ width: size, height: size }}
          aria-label={hasImage ? 'Change image' : 'Upload image'}
        >
          {imageElement}
          {isLoading && (
            <div
              className={classNames(
                'absolute inset-0 flex items-center justify-center bg-gray-200/50',
                isCircle ? 'rounded-full' : 'rounded-lg',
              )}
            >
              <CircleNotchIcon className="w-4 h-4 text-gray-800 animate-spin" />
            </div>
          )}
          {/* Hover overlay */}
          {!isLoading && !disabled && hasImage && (
            <div
              className={classNames(
                'absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity',
                isCircle ? 'rounded-full' : 'rounded-lg',
              )}
            >
              <CameraIcon className="w-4 h-4 text-white" />
            </div>
          )}
        </button>
      </div>

      {helperText && <p className="text-xs text-gray-500 text-center">{helperText}</p>}

      <input {...inputProps} />
    </div>
  );
}
